import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { HiOutlineClipboard, HiCheckCircle } from 'react-icons/hi'

export default function EmailPreview({ template, variables = {} }) {
  const [copied, setCopied] = useState(false)

  // Debug logging
  useEffect(() => {
    console.log('EmailPreview - Template:', template)
    console.log('EmailPreview - Template content:', template?.content)
    console.log('EmailPreview - Variables:', variables)
  }, [template, variables])

  // useCallbackでメモ化
  const highlightVariables = useCallback((text) => {
    if (!text) return ''
    
    // まず改行を<br>タグに変換
    let processedText = text.replace(/\n/g, '<br>')
    
    // 次に変数をハイライト
    processedText = processedText.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const value = variables[key]
      if (value && value.trim() !== '') {
        // 変数の値内の改行も処理
        const processedValue = value.replace(/\n/g, '<br>')
        return `<span class='bg-blue-100 text-blue-800 px-1 rounded font-semibold border-b-2 border-blue-300'>${processedValue}</span>`
      } else {
        return `<span class='bg-yellow-50 text-yellow-700 px-1 rounded border-b-2 border-yellow-300'>${match}</span>`
      }
    })
    
    return processedText
  }, [variables])

  const processedContent = useMemo(() => {
    return highlightVariables(template?.content || '')
  }, [template?.content, highlightVariables])

  const hasAllVariables = useMemo(() => {
    if (!template?.content) return true
    const matches = template.content.match(/\{\{(\w+)\}\}/g)
    if (!matches) return true
    return matches.every(match => {
      const key = match.replace(/[{}]/g, '')
      return variables[key] && variables[key].trim() !== ''
    })
  }, [template?.content, variables])

  const handleCopy = async () => {
    try {
      // 変数を埋め込んだテキストをコピー
      const plainText = (template?.content || '').replace(/\{\{(\w+)\}\}/g, (match, key) => variables[key] || match)
      await navigator.clipboard.writeText(plainText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Copy error:', err)
      alert('コピーに失敗しました')
    }
  }

  return (
    <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-2xl shadow-2xl border border-gray-200/50 h-full flex flex-col backdrop-blur-sm overflow-hidden">
      {/* プレミアムヘッダー */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-blue-900 bg-clip-text text-transparent">
              メールプレビュー
            </h3>
            <p className="text-xs text-gray-500 font-medium">リアルタイム表示</p>
          </div>
        </div>
        
        {/* プレミアムコピーボタン */}
        <button
          onClick={handleCopy}
          disabled={!hasAllVariables}
          className={`group flex items-center gap-3 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform ${
            hasAllVariables 
              ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl hover:scale-105' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <div className={`w-5 h-5 transition-transform ${copied ? 'scale-110' : 'group-hover:scale-110'}`}>
            {copied ? <HiCheckCircle className="w-5 h-5" /> : <HiOutlineClipboard className="w-5 h-5" />}
          </div>
          {copied ? 'コピー完了！' : 'コピー'}
          {hasAllVariables && !copied && (
            <div className="w-2 h-2 bg-white/50 rounded-full animate-pulse"></div>
          )}
        </button>
      </div>

      {/* プレミアムメール本文エリア */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="relative">
          {/* 装飾的な背景効果 */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-white to-purple-50/30 rounded-2xl"></div>
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-2xl"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-indigo-200/20 to-pink-200/20 rounded-full blur-2xl"></div>
          
          {/* メール本文コンテナ */}
          <div className="relative bg-white/80 backdrop-blur-sm border-2 border-white/50 rounded-2xl p-8 min-h-[500px] shadow-xl">
            <div className="max-w-none text-gray-900 leading-relaxed whitespace-pre-wrap">
              <div 
                dangerouslySetInnerHTML={{ __html: processedContent }} 
                style={{ 
                  lineHeight: '1.8',
                  fontSize: '15px'
                }}
                className="prose prose-gray max-w-none"
              />
            </div>
            
            {/* 空状態の装飾 */}
            {!template?.content && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-4 opacity-50">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-400 font-medium">テンプレートを選択してください</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* プレミアムステータスバー */}
      <div className="p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/80 to-blue-50/80 backdrop-blur-sm rounded-b-2xl">
        {!hasAllVariables ? (
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200/50">
            <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-amber-400 rounded-lg flex items-center justify-center shadow-md">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-orange-800 font-semibold">変数入力が必要です</p>
              <p className="text-xs text-orange-600">左側のパネルですべての変数を入力してください</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200/50">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center shadow-md">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-green-800 font-semibold">メール準備完了</p>
              <p className="text-xs text-green-600">コピーボタンでクリップボードにコピーできます</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}